import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { pipe, switchMap, tap } from 'rxjs';
import { withRequestStatus } from '../../../global/store/features/request-status.feature';
import { TasksApi } from '../../api/tasks.api';
import { Role, UserContract } from '../../../global/store/authentication/types';
import {
  CreateTaskContract,
  TaskContract,
  TaskCountsContract,
  TaskStatus,
  TaskStatusFilter,
  UpdateTaskContract,
} from './types';

type TasksState = {
  myTasks: TaskContract[] | undefined;
  assignedTasks: TaskContract[] | undefined;
  counts: TaskCountsContract | undefined;
  navMyOpen: number | undefined;
  clusterMembers: UserContract[] | undefined;
  orgSalesOrgAdmins: UserContract[] | undefined;
  accountTasks: TaskContract[] | undefined;
};

const initialState: TasksState = {
  myTasks: undefined,
  assignedTasks: undefined,
  counts: undefined,
  navMyOpen: undefined,
  clusterMembers: undefined,
  orgSalesOrgAdmins: undefined,
  accountTasks: undefined,
};

type ClusterContext = {
  salesOrganizationId: string;
  clusterId: string;
};

export const TasksStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus([
    'getMyTasks',
    'getAssignedTasks',
    'getTasksForAccount',
    'getCounts',
    'getNavOrgOpen',
    'getClusterMembers',
    'getOrgSalesOrgAdmins',
    'createTask',
    'updateTask',
    'deleteTask',
  ]),
  withMethods((store, tasksApi = inject(TasksApi)) => ({
    getMyTasks: rxMethod<
      ClusterContext & { status: TaskStatusFilter; userId?: string }
    >(
      pipe(
        switchMap(({ salesOrganizationId, clusterId, status, userId }) => {
          patchState(store, { myTasks: undefined });
          return tasksApi
            .listTasks(salesOrganizationId, clusterId, 'mine', status, userId)
            .pipe(
              store.handleActionPipe('getMyTasks', undefined),
              tap((myTasks) => patchState(store, { myTasks })),
            );
        }),
      ),
    ),

    getAssignedTasks: rxMethod<
      ClusterContext & { status: TaskStatusFilter; userId?: string }
    >(
      pipe(
        switchMap(({ salesOrganizationId, clusterId, status, userId }) => {
          patchState(store, { assignedTasks: undefined });
          return tasksApi
            .listTasks(
              salesOrganizationId,
              clusterId,
              'assigned',
              status,
              userId,
            )
            .pipe(
              store.handleActionPipe('getAssignedTasks', undefined),
              tap((assignedTasks) => patchState(store, { assignedTasks })),
            );
        }),
      ),
    ),

    getTasksForAccount: rxMethod<
      ClusterContext & { accountId: string; status: TaskStatusFilter }
    >(
      pipe(
        switchMap(({ salesOrganizationId, clusterId, accountId, status }) => {
          patchState(store, { accountTasks: undefined });
          return tasksApi
            .listTasksForAccount(
              salesOrganizationId,
              clusterId,
              accountId,
              status,
            )
            .pipe(
              store.handleActionPipe('getTasksForAccount', undefined),
              tap((accountTasks) => patchState(store, { accountTasks })),
            );
        }),
      ),
    ),

    clearAccountTasks: (): void => {
      patchState(store, { accountTasks: undefined });
    },

    getCounts: rxMethod<ClusterContext & { userId?: string }>(
      pipe(
        switchMap(({ salesOrganizationId, clusterId, userId }) => {
          return tasksApi
            .getCounts(salesOrganizationId, clusterId, userId)
            .pipe(
              store.handleActionPipe('getCounts', undefined),
              tap((counts) => patchState(store, { counts })),
            );
        }),
      ),
    ),

    getNavOrgOpen: rxMethod<{
      salesOrganizationId: string;
      clusterId?: string;
    }>(
      pipe(
        switchMap(({ salesOrganizationId, clusterId }) => {
          if (clusterId) {
            return tasksApi.getCounts(salesOrganizationId, clusterId).pipe(
              store.handleActionPipe('getNavOrgOpen', undefined),
              tap((counts) => patchState(store, { navMyOpen: counts.myOpen })),
            );
          }
          return tasksApi.getOrgCounts(salesOrganizationId).pipe(
            store.handleActionPipe('getNavOrgOpen', undefined),
            tap((counts) => patchState(store, { navMyOpen: counts.totalOpen })),
          );
        }),
      ),
    ),

    getClusterMembers: rxMethod<ClusterContext>(
      pipe(
        switchMap(({ salesOrganizationId, clusterId }) => {
          return tasksApi
            .getClusterMembers(salesOrganizationId, clusterId)
            .pipe(
              store.handleActionPipe('getClusterMembers', undefined),
              tap((clusterMembers) => patchState(store, { clusterMembers })),
            );
        }),
      ),
    ),

    getOrgSalesOrgAdmins: rxMethod<{ salesOrganizationId: string }>(
      pipe(
        switchMap(({ salesOrganizationId }) => {
          return tasksApi
            .getOrgUsers(salesOrganizationId, [Role.SALES_ORG_ADMIN])
            .pipe(
              store.handleActionPipe('getOrgSalesOrgAdmins', undefined),
              tap((orgSalesOrgAdmins) =>
                patchState(store, { orgSalesOrgAdmins }),
              ),
            );
        }),
      ),
    ),

    createTask: rxMethod<
      ClusterContext & { contract: CreateTaskContract; viewingUserId: string }
    >(
      pipe(
        switchMap(
          ({ salesOrganizationId, clusterId, contract, viewingUserId }) => {
            return tasksApi
              .createTask(salesOrganizationId, clusterId, contract)
              .pipe(
                store.handleActionPipe('createTask', undefined),
                tap((task) => {
                  const isViewingAssignee = task.assignee.id === viewingUserId;
                  const isViewingAssigner = task.assigner.id === viewingUserId;
                  patchState(store, (state) => ({
                    myTasks: isViewingAssignee
                      ? [task, ...(state.myTasks ?? [])]
                      : state.myTasks,
                    assignedTasks: isViewingAssigner
                      ? [task, ...(state.assignedTasks ?? [])]
                      : state.assignedTasks,
                    counts: state.counts
                      ? {
                          ...state.counts,
                          myOpen:
                            state.counts.myOpen + (isViewingAssignee ? 1 : 0),
                          assignedOpen:
                            state.counts.assignedOpen +
                            (isViewingAssigner ? 1 : 0),
                          totalOpen: state.counts.totalOpen + 1,
                        }
                      : state.counts,
                  }));
                }),
              );
          },
        ),
      ),
    ),

    updateTask: rxMethod<
      ClusterContext & {
        id: string;
        contract: UpdateTaskContract;
        currentUserId: string;
      }
    >(
      pipe(
        switchMap(
          ({ salesOrganizationId, clusterId, id, contract, currentUserId }) => {
            return tasksApi
              .updateTask(salesOrganizationId, clusterId, id, contract)
              .pipe(
                store.handleActionPipe('updateTask', undefined),
                tap((updated) => {
                  patchState(store, (state) => ({
                    myTasks: replaceById(state.myTasks, updated),
                    assignedTasks: replaceById(state.assignedTasks, updated),
                    accountTasks: replaceById(state.accountTasks, updated),
                    counts: recomputeCounts(
                      state.counts,
                      state.myTasks,
                      state.assignedTasks,
                      updated,
                      currentUserId,
                    ),
                  }));
                }),
              );
          },
        ),
      ),
    ),

    deleteTask: rxMethod<ClusterContext & { id: string }>(
      pipe(
        switchMap(({ salesOrganizationId, clusterId, id }) => {
          return tasksApi.deleteTask(salesOrganizationId, clusterId, id).pipe(
            store.handleActionPipe('deleteTask', undefined),
            tap(() => {
              patchState(store, (state) => {
                const removed =
                  state.myTasks?.find((t) => t.id === id) ??
                  state.assignedTasks?.find((t) => t.id === id);
                const wasOpenMine =
                  removed?.status === TaskStatus.OPEN &&
                  state.myTasks?.some((t) => t.id === id);
                const wasOpenAssigned =
                  removed?.status === TaskStatus.OPEN &&
                  state.assignedTasks?.some((t) => t.id === id);
                return {
                  myTasks: state.myTasks?.filter((t) => t.id !== id),
                  assignedTasks: state.assignedTasks?.filter(
                    (t) => t.id !== id,
                  ),
                  counts: state.counts
                    ? {
                        ...state.counts,
                        myOpen: Math.max(
                          0,
                          state.counts.myOpen - (wasOpenMine ? 1 : 0),
                        ),
                        assignedOpen: Math.max(
                          0,
                          state.counts.assignedOpen - (wasOpenAssigned ? 1 : 0),
                        ),
                        totalOpen:
                          removed?.status === TaskStatus.OPEN
                            ? Math.max(0, state.counts.totalOpen - 1)
                            : state.counts.totalOpen,
                        totalClosed:
                          removed?.status === TaskStatus.CLOSED
                            ? Math.max(0, state.counts.totalClosed - 1)
                            : state.counts.totalClosed,
                      }
                    : state.counts,
                };
              });
            }),
          );
        }),
      ),
    ),
  })),
);

function replaceById(
  list: TaskContract[] | undefined,
  updated: TaskContract,
): TaskContract[] | undefined {
  if (!list) return list;
  return list.map((t) => (t.id === updated.id ? updated : t));
}

function recomputeCounts(
  counts: TaskCountsContract | undefined,
  myTasksBefore: TaskContract[] | undefined,
  assignedTasksBefore: TaskContract[] | undefined,
  updated: TaskContract,
  currentUserId: string,
): TaskCountsContract | undefined {
  if (!counts) return counts;
  const previousMine = myTasksBefore?.find((t) => t.id === updated.id);
  const previousAssigned = assignedTasksBefore?.find(
    (t) => t.id === updated.id,
  );
  const previous = previousMine ?? previousAssigned;

  let { myOpen, assignedOpen, totalOpen, totalClosed } = counts;

  if (updated.assignee.id === currentUserId && previousMine) {
    if (
      previousMine.status === TaskStatus.OPEN &&
      updated.status === TaskStatus.CLOSED
    ) {
      myOpen = Math.max(0, myOpen - 1);
    } else if (
      previousMine.status === TaskStatus.CLOSED &&
      updated.status === TaskStatus.OPEN
    ) {
      myOpen += 1;
    }
  }
  if (updated.assigner.id === currentUserId && previousAssigned) {
    if (
      previousAssigned.status === TaskStatus.OPEN &&
      updated.status === TaskStatus.CLOSED
    ) {
      assignedOpen = Math.max(0, assignedOpen - 1);
    } else if (
      previousAssigned.status === TaskStatus.CLOSED &&
      updated.status === TaskStatus.OPEN
    ) {
      assignedOpen += 1;
    }
  }
  if (previous) {
    if (
      previous.status === TaskStatus.OPEN &&
      updated.status === TaskStatus.CLOSED
    ) {
      totalOpen = Math.max(0, totalOpen - 1);
      totalClosed += 1;
    } else if (
      previous.status === TaskStatus.CLOSED &&
      updated.status === TaskStatus.OPEN
    ) {
      totalClosed = Math.max(0, totalClosed - 1);
      totalOpen += 1;
    }
  }
  return { myOpen, assignedOpen, totalOpen, totalClosed };
}

export type { TasksState };
